import { buildProfilePayload } from '../../utils/profilePayload';
import { getProfileUserId } from '../../utils/identityUtils';
import { getItem, saveSharingConfig } from '../storage/userDataStorage';
import { sendMessage } from '../chat/chatService';
import { loadDataMessages } from '../chat/messageStore';
import { getConnectionProfiles } from '../activeSession';

/**
 * The Conversion Grant — the ONE sanctioned share-granting write outside an
 * explicit share action (user ruling 2026-07-14, CLAUDE-PROJECT §Core
 * Guarantees): converting Anon→Profile announces the conversion to EVERY
 * existing connection, granting full name + hometown (when set). Every Anon
 * connection is an in-person meet; converting through one is an active
 * upgrade of presence — not the silent leak the consent rule guards against.
 *
 * Scope discipline:
 * - The grant lives ONLY here. Refresh/enrichment paths never add
 *   sharedFields flags; display surfaces gate on the flag (isHometownShared).
 * - Sender-declared: each room's write updates the sender's OWN
 *   sent_shared_fields ledger, so the record stays truthful.
 * - A hometown added AFTER conversion is NOT auto-granted — later sharing
 *   goes through the normal sharing UI.
 * - Native Profiles (never converted) get no grant.
 *
 * Used by BOTH conversion flows — in-session (handleDone) and OTP-first
 * (handleFirstTimeProfileDone) — so they can't drift. Without the
 * announcement, the OTP-first peer only learns name/photo from a profile
 * fetch that races the signup form (it fired before the hometown was typed,
 * 2026-07-05 repro).
 */

/**
 * A room's prior anon-era shares, read from its own data messages.
 *
 * Latest own share wins (loadDataMessages returns ascending). Both payload
 * shapes are honored: 'contact' carries anonData; card-style contact_share
 * and profile_data_response carry profileData.
 *
 * Pure — exported for tests.
 *
 * @param {Array} dataMessages - deserialized data messages, ascending by time
 * @param {string} ownSenderId - the person's profile_user_id (messages are
 *   re-keyed to it by the convert RPC before this runs)
 * @returns {string[]} the room's last-declared sharedFields, or []
 */
export const extractOwnSharedFields = (dataMessages, ownSenderId) => {
  for (let i = dataMessages.length - 1; i >= 0; i--) {
    const msg = dataMessages[i];
    if (msg.senderId !== ownSenderId) continue;
    const fields = msg.anonData?.sharedFields ?? msg.profileData?.sharedFields;
    if (Array.isArray(fields)) return fields;
  }
  return [];
};

/**
 * Announce the conversion to one room: one authoritative deterministic-ID
 * contact write — the room's anon-era shares PLUS the grant fields, never a
 * partial payload, so the upsert can't wipe earlier shares (CLAUDE-PROJECT
 * §deterministic-ID race) — then the truthful ledger write. A retried or
 * reloaded announcement upserts the same row: idempotent.
 */
const announceToRoom = async (chatRoomId, profile, anonShares) => {
  try {
    const hometownGrant = profile.currentCity?.trim() ? ['currentCity'] : [];
    const fields = [...new Set([...anonShares, 'name', 'photoUrl', ...hometownGrant])];
    // Full name is the grant's form upgrade (first → full): explicit name
    // override + marker, same pattern as commitSharedFields — peers display
    // the payload's name field.
    const conversionData = buildProfilePayload(profile, fields, {
      name: profile.name,
      nameSharing: 'full',
    });
    await sendMessage(chatRoomId, {
      type: 'contact',
      sender: 'upgraded_user',
      senderId: getProfileUserId(),
      anonData: conversionData,
      senderProfileUserId: getProfileUserId(),
      timestamp: new Date().toISOString(),
    });
    // Truthful record of the grant (drives the SHARED pills + overlay restore)
    await saveSharingConfig(chatRoomId, {
      sentSharedFields: [...new Set([...anonShares, ...hometownGrant])],
      nameSharing: 'full',
    });
    console.log('[CONVERSION_GRANT] Announced to room:', chatRoomId);
  } catch (err) {
    console.error('[CONVERSION_GRANT] Announcement failed for room:', chatRoomId, err);
  }
};

/**
 * Grant to ALL of the person's rooms.
 *
 * Post-rekey (convert_pending_profile v3, awaited during OTP verification —
 * strictly before the profile form's Done in both flows) every room is keyed
 * to the person, so get_connection_profiles enumerates them all; every Anon
 * peer is a Profile user. The origin room reads its anon-era shares from the
 * local ledger (this device's own record); other rooms may have formed on
 * other devices/storage containers and connection_data is NOT re-keyed
 * (migration 021), so their prior shares are sourced from each room's
 * re-keyed chat_messages. Rooms with no prior shares get name/photo/hometown
 * only. Per-room failures are caught inside announceToRoom — one bad room
 * never blocks the rest.
 *
 * @param {string|null} originChatRoomId - the room the person converted through
 * @param {Object} profile - the person's cleaned profile data (form state)
 */
export const announceConversionGrant = async (originChatRoomId, profile) => {
  const myId = getProfileUserId();
  const rooms = await getConnectionProfiles(myId); // [] on error — origin still announces below
  const roomIds = new Set(rooms.map((r) => r.chat_room_id));
  if (originChatRoomId) roomIds.add(originChatRoomId);
  // Rooms announce concurrently: they're independent (deterministic-ID races
  // are same-room only) and the whole grant sits on the signup Done tap.
  // Within a room, read → write stays sequential.
  await Promise.all(
    [...roomIds].map(async (roomId) => {
      const anonShares =
        roomId === originChatRoomId
          ? (await getItem(`sent_shared_fields_${roomId}`)) || []
          : extractOwnSharedFields(await loadDataMessages(roomId), myId);
      await announceToRoom(roomId, profile, anonShares);
    })
  );
};
